import { FrameCacheEntry } from './types';

/**
 * Кэш OffscreenCanvas для каждого видео-элемента.
 *
 * Хранит последний отрисованный кадр участника в OffscreenCanvas.
 * При dirty-кадре: копируем из video -> offscreen cache -> main canvas.
 * При clean-кадре: рисуем из offscreen cache -> main canvas (дешевле, чем video -> canvas).
 *
 * Кэш размером с область отрисовки участника.
 */
export class FrameCache {
    private _cache: Map<HTMLVideoElement, FrameCacheEntry> = new Map();

    /**
     * Возвращает существующую запись кэша или создаёт новую.
     * При изменении размеров (смена layout) — пересоздаёт canvas.
     */
    private _getOrCreate(video: HTMLVideoElement, width: number, height: number): FrameCacheEntry {
        const existing = this._cache.get(video);

        if (existing && existing.width === width && existing.height === height) {
            return existing;
        }

        // Размеры изменились или новый элемент — создаём заново
        const canvas = new OffscreenCanvas(width, height);
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            throw new Error('Failed to get 2d context from OffscreenCanvas');
        }

        const entry: FrameCacheEntry = { canvas, ctx, width, height };

        this._cache.set(video, entry);

        return entry;
    }

    /** Есть ли запись кэша для данного видео */
    public has(video: HTMLVideoElement): boolean {
        return this._cache.has(video);
    }

    /** Удалить запись кэша для видео (вызывается при удалении видео из DOM) */
    public remove(video: HTMLVideoElement): void {
        this._cache.delete(video);
    }

    /** Очистка всех ресурсов */
    public destroy(): void {
        this._cache.clear();
    }

    /** Количество записей в кэше */
    public get size(): number {
        return this._cache.size;
    }

    /**
     * Определяет источник отрисовки для участника.
     * Dirty или первый кадр: video → cache → drawMedia (обновляем кэш и рисуем из него).
     * Non-dirty: cache → drawMedia (рисуем из готового кэша).
     */
    public resolveDrawSource(
        video: HTMLVideoElement,
        dirtyVideos: ReadonlySet<HTMLVideoElement> | undefined,
        width: number,
        height: number
    ): HTMLVideoElement | OffscreenCanvas {
        const isDirty = !dirtyVideos || dirtyVideos.has(video);

        if (isDirty || !this.has(video)) {
            // Обновляем кэш и рисуем из него
            const entry = this._getOrCreate(video, width, height);
            entry.ctx.drawImage(video, 0, 0, entry.width, entry.height);
            return entry.canvas;
        }

        // Non-dirty: рисуем из кэша
        return this._getOrCreate(video, width, height).canvas;
    }
}
